import { useTranslation } from 'react-i18next';
import { TeModal, TeContainer, TeButton, TeText, TeContainerSlim } from '@topcon/tuicl-react';

import { CombinedUser } from '../types';
import { useAppService } from '../../../providers/app/app.context';
import { resolveProfileDisplayName } from '../../../utils/profiles';

interface Props {
  user: CombinedUser | undefined;
  profiles: string[];
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

function UserConfirmModal({ user, profiles, open, loading, onClose, onSubmit }: Props) {
  const { t } = useTranslation('users');
  const { availableProfiles } = useAppService();

  // Filter to only the available profiles that are selected, so we can display
  // the API-provided display name for each.
  const filteredProfiles = availableProfiles.filter((availableProfile) =>
    profiles.includes(availableProfile.profileId)
  );

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="overlay"
      teclose={onClose}
      modalTitle={t('confirmUserModal.title')}
      contentPadding={0}
      closeable
      size="s"
      width="s"
    >
      <TeContainer backgroundColor="background.primary" padding={['m', 's']} column grow>
        <TeContainerSlim>
          <TeText size={12}>{t('confirmUserModal.infoText', { name: user?.name })}</TeText>
        </TeContainerSlim>
        <TeContainerSlim column fill gap="xs">
          {filteredProfiles.map((profile) => (
            <TeContainer
              key={profile.profileId}
              fill
              backgroundColor="background.tertiary"
              borderRadius={4}
              alignItems="center"
              justifyContent="space-between"
              padding={[0, 'm']}
              height={40}
            >
              <TeText weight={700}>{resolveProfileDisplayName(profile, t)}</TeText>
            </TeContainer>
          ))}
        </TeContainerSlim>
      </TeContainer>

      <TeButton
        slot="secondary"
        text={t('cancel')}
        variant="neutral"
        teclick={onClose}
        size="s"
        disabled={loading}
      />
      <TeButton
        slot="primary"
        text={t('confirmUserModal.submitButton')}
        variant="primary"
        size="s"
        loading={loading}
        disabled={loading}
        teclick={onSubmit}
      />
    </TeModal>
  );
}

export default UserConfirmModal;
